import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { BACKEND_URL, UPLOADS_URL } from '../config';
import { ShoppingCart, Hammer, Wrench, Monitor, Palette, Megaphone, Truck, UtensilsCrossed, FolderOpen, Briefcase, Sparkles, Mail, Globe, Search, X, User, Check, RefreshCw, Phone } from 'lucide-react';

const ServicesList = () => {
  const { user } = useContext(AuthContext);
  const [allServices, setAllServices] = useState([]);
  const [displayedServices, setDisplayedServices] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [serviceType, setServiceType] = useState('all'); // 'all', 'official', 'pro'
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [showFilters, setShowFilters] = useState(false);

  const canAccessWorkspace = user && (user.role === 'author' || user.role === 'admin');

  const handleWorkspaceClick = (e) => {
    if (!canAccessWorkspace) {
      e.preventDefault();
      setShowAuthorModal(true);
    }
  };
  
  const ITEMS_PER_PAGE = 12;
  const observerRef = useRef();
  const lastServiceRef = useRef();

  // Catégories avec icônes et couleurs
  const categoryConfig = {
    'commerce': { icon: ShoppingCart, label: 'Commerce', color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-100', text: 'text-blue-700' },
    'artisanat': { icon: Hammer, label: 'Artisanat', color: 'from-amber-500 to-orange-500', bg: 'bg-amber-100', text: 'text-amber-700' },
    'services': { icon: Wrench, label: 'Services', color: 'from-slate-500 to-gray-600', bg: 'bg-slate-100', text: 'text-slate-700' },
    'dev': { icon: Monitor, label: 'Développement', color: 'from-violet-500 to-purple-600', bg: 'bg-violet-100', text: 'text-violet-700' },
    'design': { icon: Palette, label: 'Design', color: 'from-pink-500 to-rose-500', bg: 'bg-pink-100', text: 'text-pink-700' },
    'marketing': { icon: Megaphone, label: 'Marketing', color: 'from-green-500 to-emerald-500', bg: 'bg-green-100', text: 'text-green-700' },
    'transport': { icon: Truck, label: 'Transport', color: 'from-indigo-500 to-blue-600', bg: 'bg-indigo-100', text: 'text-indigo-700' },
    'restauration': { icon: UtensilsCrossed, label: 'Restauration', color: 'from-red-500 to-orange-500', bg: 'bg-red-100', text: 'text-red-700' },
    'other': { icon: FolderOpen, label: 'Autre', color: 'from-gray-500 to-slate-500', bg: 'bg-gray-100', text: 'text-gray-700' }
  };

  const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ');
  };

  const smartSearch = (service, searchTerms) => {
    if (!searchTerms.length) return true;
    
    const title = normalizeText(service.titre);
    const desc = normalizeText(service.description);
    const cat = normalizeText(service.categorie);
    const provider = normalizeText(service.provider_nom || service.nom || service.user_nom);
    const location = normalizeText(`${service.ville || ''} ${service.pays || ''} ${service.localisation || ''}`);
    
    const allFields = `${title} ${desc} ${cat} ${provider} ${location}`;
    
    return searchTerms.every(term => {
      if (allFields.includes(term)) return true;
      if (term.length > 3) {
        const words = allFields.split(/\s+/);
        return words.some(word => {
          if (word.length < 3) return false;
          if (word.startsWith(term) || term.startsWith(word)) return true;
          if (Math.abs(word.length - term.length) <= 1) {
            let diff = 0;
            const minLen = Math.min(word.length, term.length);
            for (let i = 0; i < minLen; i++) {
              if (word[i] !== term[i]) diff++;
              if (diff > 1) return false;
            }
            return diff <= 1;
          }
          return false;
        });
      }
      return false;
    });
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const loadServices = useCallback(async () => {
    try {
      const res = await api.get('/services');
      const servicesData = Array.isArray(res.data) ? res.data : [];
      const shuffledServices = shuffleArray(servicesData);
      setAllServices(shuffledServices);
      const categories = [...new Set(servicesData.map(s => s.categorie).filter(Boolean))];
      setAvailableCategories(categories);
      setDisplayedServices(shuffledServices.slice(0, ITEMS_PER_PAGE));
      setHasMore(shuffledServices.length > ITEMS_PER_PAGE);
    } catch (err) {
      console.error(err);
      setAllServices([]);
      setDisplayedServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const filteredServices = useMemo(() => allServices.filter(s => {
    const searchTerms = normalizeText(search).split(/\s+/).filter(t => t.length > 0);
    const matchesSearch = smartSearch(s, searchTerms);
    const matchesCategory = category === 'all' || s.categorie === category;
    const isOfficial = s.is_admin_service === 1 || s.is_admin_service === '1';
    const matchesType = serviceType === 'all' || 
      (serviceType === 'official' && isOfficial) || 
      (serviceType === 'pro' && !isOfficial);
    return matchesSearch && matchesCategory && matchesType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [allServices, search, category, serviceType]);

  useEffect(() => {
    setDisplayedServices(filteredServices.slice(0, ITEMS_PER_PAGE));
    setHasMore(filteredServices.length > ITEMS_PER_PAGE);
  }, [filteredServices]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      const currentLength = displayedServices.length;
      const nextServices = filteredServices.slice(currentLength, currentLength + ITEMS_PER_PAGE);
      
      if (nextServices.length > 0) {
        setDisplayedServices(prev => [...prev, ...nextServices]);
        setHasMore(currentLength + nextServices.length < filteredServices.length);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    }, 300);
  }, [displayedServices, filteredServices, loadingMore, hasMore]);

  useEffect(() => {
    if (loading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    
    observerRef.current = observer;
    
    if (lastServiceRef.current) {
      observer.observe(lastServiceRef.current);
    }
    
    return () => observer.disconnect();
  }, [loading, hasMore, loadingMore, loadMore]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryConfig = (cat) => categoryConfig[cat] || categoryConfig.other;

  const resetFilters = () => {
    setSearch('');
    setCategory('all');
    setServiceType('all');
  };

  const activeFiltersCount = (search ? 1 : 0) + (category !== 'all' ? 1 : 0) + (serviceType !== 'all' ? 1 : 0);

  // Stats
  const officialCount = allServices.filter(s => s.is_admin_service === 1 || s.is_admin_service === '1').length;
  const proCount = allServices.length - officialCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex flex-col items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-orange-200 rounded-full animate-pulse"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="mt-6 text-orange-600 font-medium animate-pulse">Chargement des services...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMCAwaDQwdjQwSDB6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 py-12 relative">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-black mb-3 flex items-center gap-3">
                <span className="bg-white/20 backdrop-blur-sm rounded-2xl p-3"><Briefcase className="w-8 h-8" /></span>
                Services
              </h1>
              <p className="text-white/90 text-lg md:text-xl max-w-xl">
                Decouvrez des services professionnels et des prestataires de qualités
              </p>
              
              {/* Stats rapides */}
              <div className="flex flex-wrap gap-4 mt-6">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                  <span className="text-2xl font-bold">{allServices.length}</span>
                  <span className="text-white/80 ml-2">Services</span>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                  <span className="text-2xl font-bold">{officialCount}</span>
                  <span className="text-white/80 ml-2">✓ Officiels</span>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                  <span className="text-2xl font-bold">{proCount}</span>
                  <span className="text-white/80 ml-2 inline-flex items-center gap-1"><User className="w-4 h-4" /> Pros</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              {/* Mes services - toujours visible */}
              {user && (user.role === 'author' || user.role === 'admin') ? (
                <Link
                  to="/my-services"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-orange-600 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  <Sparkles className="w-5 h-5" />
                  Mes services
                </Link>
              ) : (
                <button
                  onClick={handleWorkspaceClick}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-orange-600 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  <Sparkles className="w-5 h-5" />
                  Mes services
                </button>
              )}

              {/* Demandes reçues - toujours visible */}
              {user && (user.role === 'author' || user.role === 'admin') ? (
                <Link
                  to="/pro/service-requests"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-purple-600 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  <Mail className="w-5 h-5" />
                  Gerer les demandes des clients
                </Link>
              ) : (
                <button
                  onClick={handleWorkspaceClick}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-purple-600 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  <Mail className="w-5 h-5" />
                  Gerer les demandes des clients
                </button>
              )}

              {/* Sites vitrine - nouveau module */}
              {user && (user.role === 'author' || user.role === 'admin') ? (
                <Link
                  to="/showcase"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-indigo-600 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  <Globe className="w-5 h-5" />
                  Sites vitrine
                </Link>
              ) : (
                <button
                  onClick={handleWorkspaceClick}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-indigo-600 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  <Globe className="w-5 h-5" />
                  Sites vitrine
                </button>
              )}

              <Link
                to="/my-service-requests"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/30 transition-all"
              >
                <span></span>
                Mes demandes
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Banniere Workspace */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-2xl p-5 mb-8 shadow-xl relative overflow-hidden group hover:shadow-2xl transition-shadow">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                 <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div className="text-white">
                <div className="text-xl font-bold">Mon Espace Business</div>
                <div className="text-sm text-white/80">Clients, factures, devis, stocks, comptabilite</div>
              </div>
            </div>
            {canAccessWorkspace ? (
              <Link 
                to="/workspace"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-700 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
              >
                Acceder au Workspace
                <span className="text-lg">→</span>
              </Link>
            ) : (
              <button 
                onClick={handleWorkspaceClick}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-700 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
              >
                <span></span>
                Acceder au Workspace
              </button>
            )}
          </div>
        </div>

        {/* Barre de recherche sticky */}
        <div className="sticky top-16 z-30 -mx-4 px-4 md:mx-0 md:px-0 py-2 bg-gradient-to-b from-orange-50/95 via-amber-50/95 to-transparent">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg p-3 md:p-4 border border-orange-100">
            <div className="flex gap-2">
              {/* Recherche */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Bouton filtres - toujours visible */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-medium transition-all ${
                  showFilters || activeFiltersCount > 0
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="hidden sm:inline">Filtres</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-white text-orange-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {/* Vue grid/list - caché sur mobile */}
              <div className="hidden sm:flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded-lg transition text-sm ${viewMode === 'grid' ? 'bg-white shadow text-orange-600' : 'text-gray-500'}`}
                >▦</button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded-lg transition text-sm ${viewMode === 'list' ? 'bg-white shadow text-orange-600' : 'text-gray-500'}`}
                >☰</button>
              </div>
            </div>

            {/* Panneau de filtres dépliable */}
            {showFilters && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                {/* Type de service */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Type</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setServiceType('all')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        serviceType === 'all'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Tous
                    </button>
                    <button
                      onClick={() => setServiceType('official')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        serviceType === 'official'
                          ? 'bg-indigo-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      ✓ Officiels
                    </button>
                    <button
                      onClick={() => setServiceType('pro')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-1 ${
                        serviceType === 'pro'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <User className="w-4 h-4" /> Pros
                    </button>
                  </div>
                </div>

                {/* Catégories */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Catégorie</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setCategory('all')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        category === 'all'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Toutes
                    </button>
                    {availableCategories.map(cat => {
                      const config = getCategoryConfig(cat);
                      return (
                        <button
                          key={cat}
                          onClick={() => setCategory(cat)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            category === cat
                              ? `bg-gradient-to-r ${config.color} text-white`
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {config.icon} {config.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Vue grid/list - visible seulement sur mobile dans les filtres */}
                <div className="sm:hidden">
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Affichage</label>
                  <div className="flex bg-gray-100 rounded-xl p-1 w-fit">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`px-4 py-1.5 rounded-lg transition text-sm ${viewMode === 'grid' ? 'bg-white shadow text-orange-600' : 'text-gray-500'}`}
                    >▦ Grille</button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-4 py-1.5 rounded-lg transition text-sm ${viewMode === 'list' ? 'bg-white shadow text-orange-600' : 'text-gray-500'}`}
                    >☰ Liste</button>
                  </div>
                </div>

                {/* Reset filtres */}
                {activeFiltersCount > 0 && (
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-gray-500">
                      <span className="font-semibold text-orange-600">{filteredServices.length}</span> résultat(s)
                    </span>
                    <button 
                      onClick={resetFilters}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Effacer les filtres
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Filtres actifs - badges compacts */}
            {!showFilters && activeFiltersCount > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {search && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                    "{search.substring(0, 12)}{search.length > 12 ? '...' : ''}"
                    <button onClick={() => setSearch('')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {serviceType !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                    {serviceType === 'official' ? <><Check className="w-3 h-3" /> Officiels</> : <><User className="w-3 h-3" /> Pros</>}
                    <button onClick={() => setServiceType('all')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {category !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                    {getCategoryConfig(category).icon}
                    <button onClick={() => setCategory('all')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                <button onClick={resetFilters} className="text-xs text-gray-400 hover:text-gray-600">
                  Effacer
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Compteur de resultats */}
        <div className="flex items-center justify-between mb-6 mt-4">
          <p className="text-gray-600">
            <span className="font-bold text-orange-600 text-xl">{filteredServices.length}</span> service(s)
            {displayedServices.length < filteredServices.length && (
              <span className="text-gray-400 ml-2">• {displayedServices.length} affiché(s)</span>
            )}
          </p>
          <button 
            onClick={() => setAllServices(shuffleArray([...allServices]))}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" /> Mélanger
          </button>
        </div>

        {/* Grille des services */}
        {displayedServices.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayedServices.map((service, index) => {
                const isOfficial = service.is_admin_service === 1 || service.is_admin_service === '1';
                const isLastItem = index === displayedServices.length - 1;
                const catConfig = getCategoryConfig(service.categorie);
                
                return (
                  <div
                    key={service.id}
                    ref={isLastItem ? lastServiceRef : null}
                    className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-orange-200"
                  >
                    {/* Image / Header */}
                    <div className="relative h-40 overflow-hidden">
                      {service.logo ? (
                        <div className={`w-full h-full bg-gradient-to-br ${isOfficial ? 'from-indigo-500 to-purple-600' : 'from-emerald-400 to-teal-500'} p-4`}>
                          <img 
                            src={`${UPLOADS_URL}logos/${service.logo}`}
                            alt={service.titre}
                            className="w-full h-full object-contain bg-white rounded-xl p-2"
                          />
                        </div>
                      ) : service.photos ? (
                        <img 
                          src={`${UPLOADS_URL}services/${JSON.parse(service.photos)[0]}`}
                          alt={service.titre}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${catConfig.color} flex items-center justify-center`}>
                          {React.createElement(catConfig.icon, { className: "w-16 h-16 opacity-50 text-white" })}
                        </div>
                      )}
                      
                      {/* Overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      
                      {/* Badge type */}
                      <div className="absolute top-3 left-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm ${
                          isOfficial 
                            ? 'bg-indigo-600/90 text-white' 
                            : 'bg-emerald-500/90 text-white'
                        }`}>
                          {isOfficial ? ' Officiel' : <><User className="w-3 h-3" /> Pro</>}
                        </span>
                      </div>

                      {/* Badge categorie */}
                      <div className="absolute top-3 right-3">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 ${catConfig.bg} ${catConfig.text}`}>
                          {React.createElement(catConfig.icon, { className: "w-3 h-3" })}
                        </span>
                      </div>

                      {/* Prix flottant */}
                      <div className="absolute bottom-3 right-3">
                        <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-xl font-bold text-orange-600 shadow-lg">
                          {Number(service.tarif || service.prix || 0).toLocaleString()} XOF
                        </span>
                      </div>
                    </div>

                    <div className="p-4">
                      {/* Vendeur (pour services Pro) */}
                      {!isOfficial && service.user_nom && (
                        <Link 
                          to={`/profile/${service.user_id}`}
                          className="flex items-center gap-2 mb-3 hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg transition group/seller"
                        >
                          {service.photo_profil ? (
                            <img
                              src={`${BACKEND_URL}/${service.photo_profil}`}
                              alt={service.user_nom}
                              className="w-8 h-8 rounded-full object-cover border-2 border-emerald-200 group-hover/seller:border-emerald-400 transition"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                              <span className="text-emerald-600 font-semibold text-sm">{service.user_nom?.[0]}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-sm font-medium text-gray-700 group-hover/seller:text-emerald-600 transition">{service.user_nom}</span>
                            {service.localisation && (
                              <p className="text-xs text-gray-400"> {service.localisation}</p>
                            )}
                          </div>
                        </Link>
                      )}

                      {/* Badge Plateforme pour services officiels */}
                      {isOfficial && (
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-lg">
                             Service Plateforme
                          </span>
                        </div>
                      )}

                      {/* Titre */}
                      <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1 group-hover:text-orange-600 transition">
                        {service.titre || service.nom}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-gray-500 text-sm mb-4 line-clamp-2">{service.description}</p>

                      {/* Contact rapide */}
                      {!isOfficial && (service.telephone || service.whatsapp) && (
                        <div className="flex gap-2 mb-4">
                          {service.telephone && (
                            <a 
                              href={`tel:${service.telephone}`}
                              className="flex-1 text-center text-xs px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition font-medium inline-flex items-center justify-center gap-1"
                            >
                              <Phone className="w-3 h-3" /> Appeler
                            </a>
                          )}
                          {service.whatsapp && (
                            <a 
                              href={`https://wa.me/${service.whatsapp.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 text-center text-xs px-3 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition font-medium"
                            >
                              � WhatsApp
                            </a>
                          )}
                        </div>
                      )}

                      {/* Action */}
                      <Link
                        to={`/services/${service.id}`}
                        className={`w-full block text-center py-3 rounded-xl font-semibold transition-all ${
                          isOfficial 
                            ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-600 hover:text-white'
                            : 'bg-orange-500 text-white hover:bg-orange-500 hover:text-white'
                        }`}
                      >
                        Ouvrir & demander →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Vue Liste */
            <div className="space-y-4">
              {displayedServices.map((service, index) => {
                const isOfficial = service.is_admin_service === 1 || service.is_admin_service === '1';
                const isLastItem = index === displayedServices.length - 1;
                const catConfig = getCategoryConfig(service.categorie);
                
                return (
                  <div
                    key={service.id}
                    ref={isLastItem ? lastServiceRef : null}
                    className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-orange-200"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="relative w-full md:w-48 h-40 md:h-auto flex-shrink-0">
                        {service.logo ? (
                          <div className={`w-full h-full bg-gradient-to-br ${isOfficial ? 'from-indigo-500 to-purple-600' : 'from-emerald-400 to-teal-500'} p-3`}>
                            <img 
                              src={`${UPLOADS_URL}logos/${service.logo}`}
                              alt={service.titre}
                              className="w-full h-full object-contain bg-white rounded-lg"
                            />
                          </div>
                        ) : service.photos ? (
                          <img 
                            src={`${UPLOADS_URL}services/${JSON.parse(service.photos)[0]}`}
                            alt={service.titre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className={`w-full h-full min-h-[120px] bg-gradient-to-br ${catConfig.color} flex items-center justify-center`}>
                            {React.createElement(catConfig.icon, { className: "w-12 h-12 opacity-50 text-white" })}
                          </div>
                        )}
                        
                        {/* Badge type */}
                        <div className="absolute top-2 left-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            isOfficial 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-emerald-500 text-white'
                          }`}>
                            {isOfficial ? '' : <User className="w-3 h-3" />}
                          </span>
                        </div>
                      </div>

                      {/* Contenu */}
                      <div className="flex-1 p-4 md:p-5 flex flex-col">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 ${catConfig.bg} ${catConfig.text}`}>
                              {React.createElement(catConfig.icon, { className: "w-3 h-3" })} {catConfig.label}
                            </span>
                          </div>
                          <span className="text-xl font-bold text-orange-600">
                            {Number(service.tarif || service.prix || 0).toLocaleString()} XOF
                          </span>
                        </div>

                        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-orange-600 transition">
                          {service.titre || service.nom}
                        </h3>
                        
                        <p className="text-gray-500 text-sm mb-3 line-clamp-2 flex-1">{service.description}</p>

                        {/* Footer */}
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100">
                          {/* Vendeur */}
                          {!isOfficial && service.user_nom ? (
                            <Link 
                              to={`/profile/${service.user_id}`}
                              className="flex items-center gap-2 hover:text-emerald-600 transition"
                            >
                              {service.photo_profil ? (
                                <img
                                  src={`${BACKEND_URL}/${service.photo_profil}`}
                                  alt={service.user_nom}
                                  className="w-8 h-8 rounded-full object-cover border-2 border-emerald-200"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                                  <span className="text-emerald-600 font-semibold text-sm">{service.user_nom?.[0]}</span>
                                </div>
                              )}
                              <span className="text-sm font-medium">{service.user_nom}</span>
                              {service.localisation && (
                                <span className="text-xs text-gray-400"> {service.localisation}</span>
                              )}
                            </Link>
                          ) : (
                            <span className="text-sm text-indigo-600 font-medium"> Service Plateforme</span>
                          )}

                          <div className="flex gap-2">
                            {!isOfficial && service.whatsapp && (
                              <a 
                                href={`https://wa.me/${service.whatsapp.replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition text-sm font-medium"
                              >
                                � WhatsApp
                              </a>
                            )}
                            <Link
                              to={`/services/${service.id}`}
                              className={`px-4 py-2 rounded-lg font-semibold transition ${
                                isOfficial 
                                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                  : 'bg-orange-500 text-white hover:bg-orange-600'
                              }`}
                            >
                              Voir details →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Etat vide */
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-12 h-12 text-orange-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Aucun service trouve</h3>
            <p className="text-gray-500 mb-6">Essayez de modifier vos criteres de recherche</p>
            <button 
              onClick={resetFilters}
              className="px-6 py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition"
            >
              Reinitialiser les filtres
            </button>
          </div>
        )}

        {/* Loading More */}
        {loadingMore && (
          <div className="flex justify-center py-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
              <span className="text-orange-600 font-medium">Chargement...</span>
            </div>
          </div>
        )}

        {/* Fin de liste */}
        {!hasMore && displayedServices.length > 0 && (
          <div className="text-center py-8">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 rounded-xl font-medium">
              <Check className="w-5 h-5" />
              Tous les services ont ete charges ({displayedServices.length})
            </div>
          </div>
        )}
      </div>

      {/* Modal Devenir Auteur */}
      {showAuthorModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95">
            <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-6 rounded-t-2xl">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-4xl">
                  
                </div>
                <div className="text-white">
                  <h2 className="text-2xl font-bold">Devenez Auteur</h2>
                  <p className="text-white/80">Debloquez l'Espace Business</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                L'<span className="font-semibold text-purple-700">Espace Business</span> est reserve aux <span className="font-semibold">Auteurs</span>.
              </p>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-yellow-500" /> Ce que vous debloquez :</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><span className="text-green-500"></span><strong>Creer des projets</strong> et recruter</li>
                  <li className="flex items-start gap-2"><span className="text-green-500"></span><strong>Gestion clients</strong>, factures, devis</li>
                  <li className="flex items-start gap-2"><span className="text-green-500"></span><strong>Suivi des paiements</strong> et creances</li>
                  <li className="flex items-start gap-2"><span className="text-green-500"></span><strong>Proposer des services</strong> professionnels</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/profile"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                  onClick={() => setShowAuthorModal(false)}
                >
                  <User className="w-5 h-5" /> Aller sur mon Profil
                </Link>
                <button
                  onClick={() => setShowAuthorModal(false)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bouton scroll top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center z-50"
          title="Remonter en haut"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ServicesList;
